/**
 * The built-in Write record skill (`builtin:write`), shared by the
 * play builder's Agents picker, its card, the fields a later step can read and
 * the Add to sequence step's values. It mirrors the API's contract
 * (`cleanse_api/utils/play_write.py`): a kind, its fixed outputs plus
 * `grounded`, and a flat price a record.
 */

export const WRITE_SKILL_ID = 'builtin:write';
export const WRITE_NAME = 'Write';
/** Flat a record, whatever it writes; the projection equals the charge. */
export const WRITE_CREDITS = 3;
export const WRITE_GUIDANCE_MAX = 500;
export const WRITE_MAX_WORDS_MIN = 10;
export const WRITE_MAX_WORDS_MAX = 300;

export type WriteKind =
  | 'first_line'
  | 'email'
  | 'linkedin_note'
  | 'call_opener'
  | 'account_brief'
  | 'crm_note';

export interface WriteOptions {
  kind: WriteKind;
  guidance?: string;
  maxWords?: number;
}

export interface WriteOutput {
  key: string;
  label: string;
  type: 'text' | 'list' | 'boolean';
}

interface WriteKindSpec {
  kind: WriteKind;
  label: string;
  maxWords: number;
  outputs: readonly WriteOutput[];
}

const GROUNDED: WriteOutput = {
  key: 'grounded',
  label: 'Grounded',
  type: 'boolean',
};

export const WRITE_KINDS: readonly WriteKindSpec[] = [
  {
    kind: 'first_line',
    label: 'First line',
    maxWords: 30,
    outputs: [{ key: 'first_line', label: 'First line', type: 'text' }],
  },
  {
    kind: 'email',
    label: 'Email',
    maxWords: 120,
    outputs: [
      { key: 'subject', label: 'Subject', type: 'text' },
      { key: 'body', label: 'Body', type: 'text' },
    ],
  },
  {
    kind: 'linkedin_note',
    label: 'LinkedIn note',
    maxWords: 50,
    outputs: [{ key: 'note', label: 'Note', type: 'text' }],
  },
  {
    kind: 'call_opener',
    label: 'Call opener',
    maxWords: 40,
    outputs: [{ key: 'opener', label: 'Opener', type: 'text' }],
  },
  {
    kind: 'account_brief',
    label: 'Account brief',
    maxWords: 150,
    outputs: [
      { key: 'brief', label: 'Brief', type: 'text' },
      { key: 'talking_points', label: 'Talking points', type: 'list' },
    ],
  },
  {
    kind: 'crm_note',
    label: 'CRM note',
    maxWords: 100,
    outputs: [{ key: 'note', label: 'Note', type: 'text' }],
  },
];

export function writeKindSpec(kind: string | undefined) {
  return WRITE_KINDS.find((spec) => spec.kind === kind);
}

/** The card's words for the skill: "Write (first line)". */
export function writeCardLabel(kind: string | undefined): string {
  const spec = writeKindSpec(kind);
  return spec ? `${WRITE_NAME} (${spec.label.toLowerCase()})` : WRITE_NAME;
}

/** Each kind's outputs plus `grounded`, for the kinds written upstream (every
 *  kind's when none is known), each key once and `grounded` last. */
export function writeOutputs(kinds: Iterable<string>): WriteOutput[] {
  const wanted = new Set(kinds);
  const specs = WRITE_KINDS.filter((spec) => wanted.has(spec.kind));
  const out: WriteOutput[] = [];
  for (const spec of specs.length ? specs : WRITE_KINDS)
    for (const output of spec.outputs)
      if (!out.some((seen) => seen.key === output.key)) out.push(output);
  return [...out, GROUNDED];
}

/** Why a Write block can't run, mirroring the API's validator, or ''. */
export function writeOptionsProblem(write: WriteOptions | undefined): string {
  if (!write || !writeKindSpec(write.kind)) return 'Pick what to write';
  if ((write.guidance ?? '').length > WRITE_GUIDANCE_MAX)
    return `Up to ${WRITE_GUIDANCE_MAX} characters`;
  const words = write.maxWords;
  if (
    words !== undefined &&
    (!Number.isInteger(words) ||
      words < WRITE_MAX_WORDS_MIN ||
      words > WRITE_MAX_WORDS_MAX)
  )
    return `${WRITE_MAX_WORDS_MIN} to ${WRITE_MAX_WORDS_MAX}`;
  return '';
}

/** Play value keys: the API's `^[a-z][a-z0-9_]{0,39}$`. */
export const PLAY_VALUE_KEY = /^[a-z][a-z0-9_]{0,39}$/;
export const PLAY_VALUES_MAX = 10;

/** "first_line" reads as "First line". */
export function playValueLabel(key: string): string {
  const words = key.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}
